import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { LayoutDashboard, Calendar, BookOpen, LogOut, Layers, Radio, Shield } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import AppBrandHeader from "@/components/AppBrandHeader";

const staffNavItems = [
  { to: "/timetable", label: "Timetable", icon: Calendar, testid: "nav-timetable" },
  { to: "/sessions",  label: "Sessions",  icon: Radio, testid: "nav-sessions" },
];

const elevatedNavItems = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/timetable", label: "Timetable", icon: Calendar, testid: "nav-timetable" },
  { to: "/sessions",  label: "Sessions",  icon: Radio, testid: "nav-sessions" },
  { to: "/classes",   label: "Classes",   icon: BookOpen, testid: "nav-classes" },
  { to: "/sections",  label: "Sections",  icon: Layers, testid: "nav-sections" },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const isElevated = Boolean(user?.is_elevated_admin);
  const canTimetableAdmin = Boolean(user?.can_access_timetable_admin);
  const navItems = isElevated ? elevatedNavItems : staffNavItems;

  const onLogout = async () => { await logout(); navigate("/login", { replace: true }); };

  return (
    <div className="min-h-screen bg-[#F8F9FA] flex">
      <aside className="hidden md:flex md:w-64 flex-col border-r border-[#E2E8F0] bg-white sticky top-0 h-screen">
        <div className="px-4 py-5 border-b border-[#E2E8F0] bg-gradient-to-b from-white to-[#FAFBFC]">
          <AppBrandHeader variant="sidebar" />
        </div>

        <nav className="flex-1 px-3 py-6 space-y-1">
          {navItems.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={item.testid}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors duration-200 ease-in-out ${
                    isActive ? "bg-[#FDECEF] text-[#C8102E]" : "text-[#475569] hover:bg-[#F1F5F9] hover:text-[#0F172A]"
                  }`
                }
              >
                <Icon className="w-4 h-4" />
                {item.label}
              </NavLink>
            );
          })}
          {canTimetableAdmin && (
            <NavLink
              to="/admin/timetable"
              data-testid="nav-admin-timetable"
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors duration-200 ease-in-out ${
                  isActive ? "bg-[#FDECEF] text-[#C8102E]" : "text-[#475569] hover:bg-[#F1F5F9] hover:text-[#0F172A]"
                }`
              }
            >
              <Shield className="w-4 h-4" />
              Timetable admin
            </NavLink>
          )}
        </nav>

        <div className="border-t border-[#E2E8F0] p-4">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-9 h-9 rounded-full bg-[#C8102E] text-white flex items-center justify-center font-bold text-sm">
              {(user?.name || "?").split(" ").map(s => s[0]).slice(0,2).join("")}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold text-[#0F172A] truncate" data-testid="user-name">{user?.name}</div>
              <div className="text-xs text-[#94A3B8] truncate">{user?.email}</div>
            </div>
          </div>
          <Button
            variant="outline"
            className="w-full border-[#E2E8F0] text-[#475569] hover:bg-[#F1F5F9] hover:text-[#0F172A]"
            onClick={onLogout}
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4 mr-2" /> Sign out
          </Button>
        </div>
      </aside>

      <div className="md:hidden fixed top-0 inset-x-0 z-50 bg-white border-b border-[#E2E8F0] backdrop-blur-xl">
        <div className="flex items-center justify-between px-4 py-3">
          <AppBrandHeader variant="compact" className="min-w-0 flex-1" />
          <Button size="sm" variant="ghost" onClick={onLogout} data-testid="logout-btn-mobile">
            <LogOut className="w-4 h-4" />
          </Button>
        </div>
        <div className="flex overflow-x-auto border-t border-[#E2E8F0]">
          {[...navItems, ...(canTimetableAdmin ? [{ to: "/admin/timetable", label: "Admin", icon: Shield }] : [])].map(item => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  `flex-1 min-w-fit flex items-center justify-center gap-2 px-4 py-2 text-xs font-medium border-b-2 transition-colors whitespace-nowrap ${
                    isActive ? "border-[#C8102E] text-[#C8102E]" : "border-transparent text-[#475569]"
                  }`
                }
              >
                <Icon className="w-3.5 h-3.5" />
                {item.label}
              </NavLink>
            );
          })}
        </div>
      </div>

      <main className="flex-1 min-w-0 md:pt-0 pt-24">
        <Outlet />
      </main>
    </div>
  );
}
